import React, { useMemo } from 'react';
import { FileText, Eye, Brain, Zap, Shield } from 'lucide-react';
import { jsonrepair } from 'jsonrepair';

/* ═══════════════════════════════════════════════════
   V2 Types — matches /api/driving/concept-report-data
   ═══════════════════════════════════════════════════ */

interface CoverData {
  num_territories: number;
  num_segments: number;
  num_agents: number;
  concepts_tested?: string[];
}

interface TerritoryScore {
  name: string;
  mean: number;
  pct: number;
}

interface BestTerritory {
  key: string;
  name: string;
  score: number;
  explanation?: string;
}

interface MostConsistentTerritory {
  key: string;
  name: string;
  wins: number;
  out_of: number;
}

interface OverallData {
  mean_appeal_score: number;
  mean_appeal_pct: number;
  mean_appeal_top2box_pct: number;
  mean_purchase_intent: number;
  purchase_intent_pct: number;
  purchase_intent_top2box_pct: number;
  mean_d_scores: Record<string, number>;
  mean_k_scores: Record<string, number>;
  territory_scores: Record<string, TerritoryScore>;
  best_territory: BestTerritory;
  most_consistent_territory: MostConsistentTerritory;
  vehicle_choice_influence?: string;
}

interface LeadingTerritory {
  key: string;
  name: string;
  score: number;
}

interface SegmentData {
  segment: string;
  rank: number;
  num_agents: number;
  mean_appeal: number;
  appeal_top2box_pct: number;
  mean_purchase_intent: number;
  purchase_intent_pct: number;
  purchase_intent_top2box_pct: number;
  leading_territory: LeadingTerritory;
  key_insight: string;
  d_scores: Record<string, number>;
  k_scores: Record<string, number>;
  score_distributions: Record<string, Record<string, number>>;
  mean_d_score?: number;
  business_summary?: string;
}

interface TerritorySegmentScore {
  segment: string;
  score: number;
}

interface TerritoryData {
  key: string;
  name: string;
  rank: number;
  concept_statement: string;
  mean_score: number;
  purchase_intent_mean: number;
  purchase_intent_pct: number;
  pct: number;
  top_segments: TerritorySegmentScore[];
  all_segments: TerritorySegmentScore[];
  vehicle_choice_influence?: string;
  explanation?: string;
}

interface ProofStyleRanking {
  rank: number;
  name: string;
  description?: string;
  mean_trust_score: number;
  pct: number;
  detail?: string;
  winning_segments?: number;
  total_segments?: number;
}

interface SegmentRTB {
  segment: string;
  winning_style: string;
  runner_up: string;
  delta: number;
}

interface ProofStyleData {
  overall_believability: number;
  overall_believability_top2box_pct: number;
  by_segment: Record<string, { believability: number; believability_top2box_pct: number }>;
  styles?: ProofStyleRanking[];
  segment_rtb?: SegmentRTB[];
}

interface SegmentInsightData {
  territory_fit?: string;
  pain_point?: string;
  mechanism_trust?: string;
  driving_experience?: string;
  concept_benefit?: string;
}

interface AgentDScoreEntry {
  answer?: string;
  score: number;
  reasoning?: string;
  response_style?: string;
}

interface AgentResult {
  agent_pid?: string;
  agent_name?: string;
  agent_id?: string;
  segment?: string;
  concept_territory?: string;
  injection_block?: string;
  framework_affinity?: boolean;
  d_scores?: Record<string, AgentDScoreEntry | number>;
}

interface V2ReportData {
  cover: CoverData;
  overall: OverallData;
  segments: SegmentData[];
  territories: TerritoryData[];
  proof_style: ProofStyleData;
  segment_insights: Record<string, SegmentInsightData>;
  agent_results: AgentResult[];
  score_distributions: Record<string, Record<string, number>>;
  executive_summary: string;
  concept_name: string;
  concept_statement: string;
  impairment_addressed: string;
}

interface LegacySummary {
  executive_summary?: string;
  concept_name?: string;
  concept_statement?: string;
  impairment_addressed?: string;
  num_agents?: number;
  avg_d_scores?: Record<string, number>;
  avg_d_by_segment?: Record<string, Record<string, number>>;
  best_territory?: string;
  agent_scores?: AgentResult[];
  segment_insights?: Record<string, SegmentInsightData> | Array<SegmentInsightData & { segment: string }>;
  config?: Record<string, any>;
}

interface ContentData {
  [key: string]: unknown;
  task?: string;
}

interface DrivingConceptTestResultsProps {
  simulationResponse: string;
  contentData: ContentData;
  isPreparingExport?: boolean;
}

/* ═══════════════════════════════
   Brand tokens
   ═══════════════════════════════ */

const BRAND = {
  accent: '#07E5D1',
  accentLight: '#E8FCFA',
  accentHover: '#06D4C2',
  dark: '#012e2a',
};

/* ═══════════════════════════════
   Territory config & helpers
   ═══════════════════════════════ */

const T_KEYS = ['d1_see_more', 'd2_understand_more', 'd3_respond_better', 'd4_feel_more_capable'] as const;

const T_CONFIG: Record<string, { label: string; color: string; bg: string; border: string; text: string; dot: string; pill: string; pillText: string; barBg: string; icon: React.ReactNode }> = {
  d1_see_more: {
    label: 'See More', color: '#2563EB', bg: 'bg-blue-50', border: 'border-blue-200',
    text: 'text-blue-700', dot: 'bg-blue-600', pill: 'bg-blue-100', pillText: 'text-blue-700',
    barBg: 'bg-blue-600', icon: <Eye className="w-4 h-4" />,
  },
  d2_understand_more: {
    label: 'Understand More', color: '#059669', bg: 'bg-emerald-50', border: 'border-emerald-200',
    text: 'text-emerald-700', dot: 'bg-emerald-600', pill: 'bg-emerald-100', pillText: 'text-emerald-700',
    barBg: 'bg-emerald-600', icon: <Brain className="w-4 h-4" />,
  },
  d3_respond_better: {
    label: 'Respond Better', color: '#D97706', bg: 'bg-amber-50', border: 'border-amber-200',
    text: 'text-amber-700', dot: 'bg-amber-600', pill: 'bg-amber-100', pillText: 'text-amber-700',
    barBg: 'bg-amber-500', icon: <Zap className="w-4 h-4" />,
  },
  d4_feel_more_capable: {
    label: 'Feel More Capable', color: '#7C3AED', bg: 'bg-violet-50', border: 'border-violet-200',
    text: 'text-violet-700', dot: 'bg-violet-600', pill: 'bg-violet-100', pillText: 'text-violet-700',
    barBg: 'bg-violet-600', icon: <Shield className="w-4 h-4" />,
  },
};

function cap(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}


/* ═══════════════════════════════
   Parser: V2 vs Legacy
   ═══════════════════════════════ */

function parseReportData(simulationResponse: string): { v2: V2ReportData | null; legacy: LegacySummary | null } {
  try {
    const data = JSON.parse(jsonrepair(simulationResponse));

    if (data.overall && data.segments && data.cover) {
      return { v2: data as V2ReportData, legacy: null };
    }

    const summary = (data.summary || data) as LegacySummary;
    const config = data.config || summary.config || {};
    if (config.concept_name && !summary.concept_name) summary.concept_name = config.concept_name;
    if (config.concept_statement && !summary.concept_statement) summary.concept_statement = config.concept_statement;
    return { v2: null, legacy: summary };
  } catch {
    return { v2: null, legacy: null };
  }
}

function legacyToV2(legacy: LegacySummary): V2ReportData | null {
  const dScores = legacy.avg_d_scores || {};
  const segMap = legacy.avg_d_by_segment || {};
  const segments = Object.keys(segMap);

  const territoryScores: Record<string, TerritoryScore> = {};
  for (const k of T_KEYS) {
    territoryScores[k] = {
      name: T_CONFIG[k]?.label || k,
      mean: dScores[k] || 0,
      pct: ((dScores[k] || 0) / 5) * 100,
    };
  }

  let bestKey = T_KEYS[0] as string;
  let bestVal = 0;
  for (const k of T_KEYS) {
    if ((dScores[k] || 0) > bestVal) { bestVal = dScores[k] || 0; bestKey = k; }
  }

  const segmentsList: SegmentData[] = segments.map((seg, i) => {
    const sd = segMap[seg] || {};
    let lk = T_KEYS[0] as string;
    let lv = 0;
    let sum = 0;
    for (const k of T_KEYS) {
      const v = sd[k] || 0;
      sum += v;
      if (v > lv) { lv = v; lk = k; }
    }
    return {
      segment: seg,
      rank: i + 1,
      num_agents: 0,
      mean_appeal: sum / T_KEYS.length,
      appeal_top2box_pct: ((sum / T_KEYS.length) / 5) * 100,
      mean_purchase_intent: 0,
      purchase_intent_pct: 0,
      purchase_intent_top2box_pct: 0,
      leading_territory: { key: lk, name: T_CONFIG[lk]?.label || lk, score: lv },
      key_insight: '',
      d_scores: sd,
      k_scores: {},
      score_distributions: {},
    };
  });

  const rawInsights = legacy.segment_insights;
  let insightsMap: Record<string, SegmentInsightData> = {};
  if (rawInsights) {
    if (Array.isArray(rawInsights)) {
      for (const ins of rawInsights) insightsMap[ins.segment] = ins;
    } else {
      insightsMap = rawInsights as Record<string, SegmentInsightData>;
    }
  }

  return {
    cover: { num_territories: 4, num_segments: segments.length, num_agents: legacy.num_agents || legacy.agent_scores?.length || 0 },
    overall: {
      mean_appeal_score: Object.values(dScores).reduce((a, b) => a + b, 0) / (Object.keys(dScores).length || 1),
      mean_appeal_pct: (Object.values(dScores).reduce((a, b) => a + b, 0) / (Object.keys(dScores).length || 1) / 5) * 100,
      mean_appeal_top2box_pct: 0,
      mean_purchase_intent: 0,
      purchase_intent_pct: 0,
      purchase_intent_top2box_pct: 0,
      mean_d_scores: dScores,
      mean_k_scores: {},
      territory_scores: territoryScores,
      best_territory: { key: bestKey, name: T_CONFIG[bestKey]?.label || bestKey, score: bestVal },
      most_consistent_territory: { key: bestKey, name: T_CONFIG[bestKey]?.label || bestKey, wins: 0, out_of: segments.length },
    },
    segments: segmentsList,
    territories: T_KEYS.map((k, i) => ({
      key: k,
      name: T_CONFIG[k]?.label || k,
      rank: i + 1,
      concept_statement: legacy.concept_statement || '',
      mean_score: dScores[k] || 0,
      purchase_intent_mean: 0,
      purchase_intent_pct: 0,
      pct: ((dScores[k] || 0) / 5) * 100,
      top_segments: segmentsList
        .map(s => ({ segment: s.segment, score: s.d_scores[k] || 0 }))
        .sort((a, b) => b.score - a.score),
      all_segments: segmentsList.map(s => ({ segment: s.segment, score: s.d_scores[k] || 0 })),
    })).sort((a, b) => b.mean_score - a.mean_score).map((t, i) => ({ ...t, rank: i + 1 })),
    proof_style: { overall_believability: 0, overall_believability_top2box_pct: 0, by_segment: {} },
    segment_insights: insightsMap,
    agent_results: legacy.agent_scores || [],
    score_distributions: {},
    executive_summary: legacy.executive_summary || '',
    concept_name: legacy.concept_name || '',
    concept_statement: legacy.concept_statement || '',
    impairment_addressed: legacy.impairment_addressed || '',
  };
}

/* ═══════════════════════════════
   Section heading helper
   ═══════════════════════════════ */

const SectionLabel: React.FC<{ num: string; label: string }> = ({ num, label }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="text-xs font-semibold text-gray-300 font-mono tabular-nums">{num}</span>
    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">{label}</span>
    <div className="flex-1 h-px bg-gray-200" />
  </div>
);

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */

const DrivingConceptTestResults: React.FC<DrivingConceptTestResultsProps> = ({
  simulationResponse,
  contentData,
}) => {
  const report = useMemo(() => {
    const { v2, legacy } = parseReportData(simulationResponse);
    if (v2) return v2;
    if (legacy) return legacyToV2(legacy);
    return null;
  }, [simulationResponse]);

  const displayName = (contentData?.name as string) || report?.concept_name || 'Concept Report';

  if (!report) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="w-8 h-8 mx-auto mb-3 text-gray-400" />
        <p>Unable to parse driving concept test results.</p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <ReportHeader report={report} displayName={displayName} />
      <OverallConceptScore report={report} />
      {report.segments.length > 0 && <SegmentScoresSection report={report} />}
      {report.territories.length > 0 && <TerritoryPerformance report={report} />}
      <BelievabilityTrust report={report} />
      <ProofStyleAnalysis report={report} />
      {/* {report.agent_results.length > 0 && <IndividualResponses agents={report.agent_results} />} */}
    </div>
  );
};

/* ═══════════════════════════════════
   REPORT HEADER (Cover)
   ═══════════════════════════════════ */

const COVER_STATS: { key: 'num_territories' | 'num_segments' | 'num_agents'; label: string; prefix?: string }[] = [
  { key: 'num_territories', label: 'Benefit territories' },
  { key: 'num_segments', label: 'Segments tested' },
  { key: 'num_agents', label: 'Consumer twins', prefix: 'N=' },
];

const ReportHeader: React.FC<{ report: V2ReportData; displayName: string }> = ({ report, displayName }) => (
  <div className="rounded-lg shadow-sm overflow-hidden" style={{ backgroundColor: BRAND.dark }}>
    <div className="px-8 pt-8 pb-6">
      {/* Top bar */}
        <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-3">
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.09em] px-3 py-1.5 rounded-full text-white"
            style={{ backgroundColor: BRAND.accent }}
          >
            BluePill AI
          </span>
        </div>
        {/* <div className="text-[11px] text-white/35 text-right leading-relaxed">
          {report.concept_name && <>Concept: {report.concept_name}<br /></>}
          {report.impairment_addressed && <>Impairment: {cap(report.impairment_addressed)}</>}
        </div> */}
      </div>

      {/* Title block */}
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-4">Consumer Concept Testing Report</div>
        <h2 className="text-3xl font-semibold text-white leading-tight mb-3">
          Driver Capability<br />Concept Study:<br />
          <em className="font-normal">{displayName}</em>
        </h2>
        {report.concept_statement && (
          <p className="text-sm text-white/50 leading-relaxed max-w-2xl">{report.concept_statement}</p>
        )}
      </div>

      {/* Big stat row */}
      <div className="flex gap-12 pt-6" style={{ borderTop: '1px solid rgba(7, 229, 209, 0.12)' }}>
        {COVER_STATS.map(s => {
          const val = report.cover[s.key];
          if (!val) return null;
          return (
            <div key={s.key}>
              <div className="text-4xl font-bold text-white leading-none mb-1 tabular-nums">{s.prefix || ''}{val}</div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-white/35">{s.label}</div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Footer note */}
    <div className="px-8 py-3 flex items-center justify-between gap-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-[10px] text-white/25 leading-relaxed">
        Produced using BluePill AI&rsquo;s synthetic consumer twin methodology. All segment responses are AI-simulated personas calibrated to real behavioral and attitudinal data.
      </p>
      <span className="text-[9px] uppercase tracking-wider text-white/20 font-semibold whitespace-nowrap">Confidential</span>
    </div>
  </div>
);

/* ═══════════════════════════════════
   SECTION 1 — Overall Concept Score
   ═══════════════════════════════════ */

const OverallConceptScore: React.FC<{ report: V2ReportData }> = ({ report }) => {
  const o = report.overall;
  const bestCfg = T_CONFIG[o.best_territory?.key];
  const terrMap = useMemo(() => {
    const m: Record<string, TerritoryData> = {};
    for (const t of report.territories) m[t.key] = t;
    return m;
  }, [report.territories]);

  return (
    <div>
      <SectionLabel num="01" label="Overall Concept Score" />

      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          How the concept <span className="text-gray-400 italic font-normal">performed overall.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Aggregate scores across all segments and four benefit territories.
        </p>
      </div>

      {/* Hero row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 mb-5">
        {/* Main score card */}
        <div className="text-white rounded-lg shadow-sm p-8 flex flex-col justify-between min-h-[260px]" style={{ backgroundColor: BRAND.dark }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] opacity-50 mb-3">Overall Appeal Score</div>
            <div className="text-6xl font-bold leading-none mb-2 tabular-nums">{o.mean_appeal_score.toFixed(1)}</div>
            <div className="text-sm opacity-55 mb-6">out of 5</div>
          </div>
          {report.executive_summary && (
            <div className="text-sm leading-relaxed opacity-75 pt-4" style={{ borderTop: '1px solid rgba(7, 229, 209, 0.2)' }}>
              {report.executive_summary}
            </div>
          )}
        </div>

        {/* Side metrics */}
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Vehicle Choice Influence</div>
            <div className="text-4xl font-bold text-gray-900 leading-none mb-1">{o.vehicle_choice_influence || '—'}</div>
            <div className="text-xs text-gray-400"><span className="tabular-nums">{o.mean_purchase_intent.toFixed(1)}</span> / 5 influence score</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Best Territory</div>
            <div className="flex items-center gap-2 mb-1">
              {bestCfg && <div className={`w-2 h-2 rounded-full ${bestCfg.dot}`} />}
              <div className="text-2xl font-bold text-gray-900 leading-none">{o.best_territory?.name || '—'}</div>
            </div>
            <div className="text-xs text-gray-400 mb-1"><span className="tabular-nums">{(o.best_territory?.score || 0).toFixed(1)}</span> / 5</div>
            {o.best_territory?.explanation && (
              <p className="text-xs text-gray-500 leading-relaxed mt-2">{o.best_territory.explanation}</p>
            )}
          </div>
        </div>
      </div>

      {/* Territory summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {T_KEYS.map(tk => {
          const ts = o.territory_scores?.[tk];
          const cfg = T_CONFIG[tk];
          if (!ts || !cfg) return null;
          const terrExplanation = terrMap[tk]?.explanation;
          return (
            <div key={tk} className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
              <div className={`w-2 h-2 rounded-full ${cfg.dot} mb-3`} />
              <div className="text-sm font-semibold text-gray-800 mb-1">{cfg.label}</div>
              <div className="text-3xl font-bold text-gray-900 leading-none mb-0.5 tabular-nums">
                {ts.mean.toFixed(1)} <span className="text-sm font-normal text-gray-400">/ 5</span>
              </div>
              <div className="h-1 bg-gray-100 rounded-full overflow-hidden mt-2">
                <div className={`h-full rounded-full ${cfg.barBg}`} style={{ width: `${Math.min(ts.pct, 100)}%` }} />
              </div>
              {terrExplanation && (
                <p className="text-[11px] text-gray-500 leading-snug mt-2">{terrExplanation}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════
   SECTION 2 — Segment Scores
   ═══════════════════════════════════ */

const SegmentScoresSection: React.FC<{ report: V2ReportData }> = ({ report }) => {
  const segs = [...report.segments].sort((a, b) => a.rank - b.rank);
  const topSeg = segs[0];
  const mc = report.overall.most_consistent_territory;
  const proof = report.proof_style;
  const insights = report.segment_insights || {};

  return (
    <div>
      <SectionLabel num="02" label="Segment Scores" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          How each audience <span className="text-gray-400 italic font-normal">responded.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Segments ranked by overall appeal score. &ldquo;Leading territory&rdquo; shows which concept resonated most; &ldquo;key insight&rdquo; summarizes the driver behind it.
        </p>
      </div>

      {/* Intro cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Highest Scoring Segment</div>
          <div className="text-xl font-bold text-gray-900 leading-tight mb-1">{cap(topSeg?.segment || '')}</div>
          <div className="text-xs text-gray-400"><span className="tabular-nums">{(topSeg?.mean_d_score || topSeg?.mean_appeal || 0).toFixed(1)}</span> / 5 overall score</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Most Consistent Territory</div>
          <div className="text-xl font-bold text-gray-900 leading-tight mb-1">{mc?.name || '—'}</div>
          <div className="text-xs text-gray-400">appeared in <span className="tabular-nums">{mc?.wins || 0}</span> of <span className="tabular-nums">{mc?.out_of || 0}</span> segment tops</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Believability</div>
          <div className="text-xl font-bold text-gray-900 leading-tight mb-1 tabular-nums">{(proof?.overall_believability || 0).toFixed(1)} <span className="text-sm font-normal text-gray-400">/ 5</span></div>
          <div className="text-xs text-gray-400">mean believability score</div>
        </div>
      </div>

      {/* Segment table */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{ width: '36px' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '23%' }} />
              <col style={{ width: '23%' }} />
            </colgroup>
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Segment</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Overall Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Leading Territory</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Driving Experience</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Mechanism Trust</th>
              </tr>
            </thead>
            <tbody>
              {segs.map(s => {
                const lt = s.leading_territory;
                const cfg = T_CONFIG[lt?.key] || T_CONFIG.d1_see_more;
                const si = insights[s.segment] || {};
                const overallScore = s.mean_d_score || s.mean_appeal || 0;
                return (
                  <tr key={s.segment} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors align-top">
                    <td className="px-4 py-4 text-xs text-gray-400 font-mono tabular-nums">{s.rank}</td>
                    <td className="px-4 py-4">
                      <div className="font-semibold text-gray-900">{cap(s.segment)}</div>
                      {s.business_summary && (
                        <p className="text-[11px] text-gray-500 leading-snug mt-1">{s.business_summary}</p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-xl font-bold text-gray-900 leading-none mb-0.5 tabular-nums">{overallScore.toFixed(1)}</div>
                      <div className="text-[10px] text-gray-400">out of 5.0</div>
                      <div className="h-1 bg-gray-100 rounded-full overflow-hidden mt-1.5">
                        <div className="h-full rounded-full" style={{ width: `${Math.min((overallScore / 5) * 100, 100)}%`, backgroundColor: BRAND.accent }} />
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.pill} ${cfg.pillText}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                        {lt?.name || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs text-gray-600 leading-relaxed">{si.driving_experience || '—'}</td>
                    <td className="px-4 py-4 text-xs text-gray-600 leading-relaxed">{si.mechanism_trust || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════
   SECTION 3 — Benefit Territory Performance
   ═══════════════════════════════════ */

const TerritoryPerformance: React.FC<{ report: V2ReportData }> = ({ report }) => {
  const terrs = [...report.territories].sort((a, b) => a.rank - b.rank);

  return (
    <div>
      <SectionLabel num="03" label="Benefit Territory Performance" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          Performance by <span className="text-gray-400 italic font-normal">concept territory.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Each territory&rsquo;s mean score, purchase intent, and which segments it won.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {terrs.map(t => {
          const cfg = T_CONFIG[t.key] || T_CONFIG.d1_see_more;
          const rankLetter = String.fromCharCode(64 + t.rank);
          return (
            <div key={t.key} className={`${cfg.bg} ${cfg.border} border rounded-lg p-6`}>
              <div className={`text-[10px] font-semibold uppercase tracking-[0.1em] ${cfg.text} mb-1`}>
                Territory {rankLetter} &middot; Ranked #{t.rank}
              </div>
              <div className="text-xl font-bold text-gray-900 mb-3">{t.name}</div>

              {t.concept_statement && (
                <p className="text-xs text-gray-500 italic leading-relaxed mb-5 pb-5 border-b border-black/[0.08]">
                  &ldquo;{t.concept_statement}&rdquo;
                </p>
              )}

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Mean Score</div>
                  <div className="text-2xl font-bold text-gray-900 leading-none tabular-nums">{t.mean_score.toFixed(1)} <span className="text-sm font-normal text-gray-400">/ 5</span></div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Vehicle Choice Influence</div>
                  <div className="text-2xl font-bold text-gray-900 leading-none">{t.vehicle_choice_influence || '—'}</div>
                </div>
              </div>

              <div className="h-1 bg-black/[0.08] rounded-full overflow-hidden mb-4">
                <div className={`h-full rounded-full ${cfg.barBg}`} style={{ width: `${Math.min(t.pct, 100)}%` }} />
              </div>

              {t.explanation && (
                <p className="text-xs text-gray-600 leading-relaxed mb-4">{t.explanation}</p>
              )}

              <div className="flex flex-wrap gap-1.5">
                {(t.all_segments || t.top_segments || []).map(s => {
                  const isTop = (t.top_segments || []).some(ts => ts.segment === s.segment);
                  return (
                    <span key={s.segment} className={`text-[10px] px-2.5 py-1 rounded-full text-gray-800 ${isTop ? 'bg-black/[0.1] font-semibold' : 'bg-black/[0.06] font-medium'}`}>
                      {cap(s.segment)}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════
   SECTION 4 — Pain Points & Believability
   ═══════════════════════════════════ */

const BelievabilityTrust: React.FC<{ report: V2ReportData }> = ({ report }) => {
  const proof = report.proof_style;
  const insights = report.segment_insights || {};
  const hasProof = proof && (proof.overall_believability > 0 || Object.keys(proof.by_segment || {}).length > 0);
  const painEntries = Object.entries(insights).filter(([, v]) => v?.pain_point);

  if (!hasProof && painEntries.length === 0) return null;

  return (
    <div>
      <SectionLabel num="04" label="Pain Points & Believability" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          What drivers <span className="text-gray-400 italic font-normal">struggle with</span> and how believable <span className="text-gray-400 italic font-normal">is the solution.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Key pain points by segment and whether the audience trusts that this system could actually deliver.
        </p>
      </div>

      {/* Pain points list */}
      {painEntries.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND.accent }} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-500">Key Pain Points by Segment</span>
          </div>
          <div className="space-y-2.5">
            {painEntries.map(([seg, ins]) => (
              <div key={seg} className="bg-white border border-gray-200 rounded-lg shadow-sm px-6 py-4 flex gap-5 items-baseline">
                <div className="min-w-[140px] font-semibold text-sm text-gray-900 flex-shrink-0">{cap(seg)}</div>
                <p className="text-sm text-gray-700 leading-relaxed">{ins.pain_point}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Believability: score card + per-segment table */}
      {hasProof && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="text-white rounded-lg shadow-sm p-7 flex flex-col justify-center" style={{ backgroundColor: BRAND.dark }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-white/45 mb-3">Overall Believability</div>
            <div className="text-5xl font-bold leading-none mb-2 tabular-nums">{(proof.overall_believability || 0).toFixed(1)}</div>
            <div className="text-xs text-white/45 mb-5">out of 5.0</div>
            <div className="h-1 bg-white/15 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${((proof.overall_believability || 0) / 5) * 100}%`, backgroundColor: BRAND.accent }} />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Segment</th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Believability</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(proof.by_segment || {}).map(([seg, ps]) => (
                  <tr key={seg}>
                    <td className="px-5 py-3 font-semibold text-gray-900">{cap(seg)}</td>
                    <td className="px-5 py-3 font-bold text-gray-900 tabular-nums">{(ps.believability || 0).toFixed(1)} <span className="text-gray-400 font-normal">/ 5</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════
   SECTION 5 — Proof Style Analysis
   (renders when proof_style.styles data is available)
   ═══════════════════════════════════ */

const ProofStyleAnalysis: React.FC<{ report: V2ReportData }> = ({ report }) => {
  const proof = report.proof_style;
  const styles = proof?.styles;
  const segRtb = proof?.segment_rtb;

  if (!styles || styles.length === 0) return null;

  const sorted = [...styles].sort((a, b) => a.rank - b.rank);

  return (
    <div>
      <SectionLabel num="05" label="Proof Style Analysis" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          Which framing <span className="text-gray-400 italic font-normal">built trust?</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Proof styles tested across all segments to determine whether mechanism-led, outcome-led, or prestige language drives higher trust and believability.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {sorted.map((style, i) => {
          const isWinner = i === 0;
          return (
            <div
              key={style.name}
              className={`rounded-lg shadow-sm p-6 ${isWinner ? 'text-white' : 'bg-white border border-gray-200'}`}
              style={isWinner ? { backgroundColor: BRAND.dark } : undefined}
            >
              <div className={`text-[10px] font-semibold uppercase tracking-[0.1em] mb-2 ${isWinner ? 'text-white/45' : 'text-gray-500'}`}>
                Proof Style {String.fromCharCode(64 + style.rank)} &middot; Ranked #{style.rank}
              </div>
              <div className={`text-base font-semibold mb-1 ${isWinner ? 'text-white' : 'text-gray-900'}`}>{style.name}</div>
              {style.description && (
                <p className={`text-xs leading-relaxed mb-5 ${isWinner ? 'text-white/55' : 'text-gray-500'}`}>{style.description}</p>
              )}
              <div className={`text-4xl font-bold leading-none mb-1 tabular-nums ${isWinner ? 'text-white' : 'text-gray-900'}`}>
                {style.pct.toFixed(0)}%
              </div>
              <div className={`text-xs mb-4 ${isWinner ? 'text-white/45' : 'text-gray-400'}`}>mean trust score</div>
              <div className={`h-1 rounded-full overflow-hidden mb-4 ${isWinner ? 'bg-white/15' : 'bg-gray-100'}`}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.min(style.pct, 100)}%`, backgroundColor: isWinner ? BRAND.accent : BRAND.dark }}
                />
              </div>
              {style.detail && (
                <p className={`text-xs leading-relaxed ${isWinner ? 'text-white/60' : 'text-gray-500'}`}>{style.detail}</p>
              )}
            </div>
          );
        })}
      </div>

      {segRtb && segRtb.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Segment</th>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Winning Proof Style</th>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Runner-Up</th>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Trust Score Delta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {segRtb.map(row => (
                <tr key={row.segment}>
                  <td className="px-5 py-3 font-semibold text-gray-900">{cap(row.segment)}</td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: BRAND.dark }}>
                      {row.winning_style}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-500">{row.runner_up}</td>
                  <td className="px-5 py-3 text-xs font-semibold tabular-nums" style={{ color: '#0d9488' }}>+{row.delta.toFixed(0)} pts</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════
   INDIVIDUAL RESPONSES
   ═══════════════════════════════════ */



export default DrivingConceptTestResults;
